import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Plus, Sparkles, Trash2, ChevronDown, ChevronUp, BookOpen, FileUp, Minus, Users, Pencil, UtensilsCrossed, Globe, Search, Zap, Heart, Image as ImageIcon, X, Download, CalendarPlus, EyeOff, Eye, Filter, Camera } from "lucide-react";
import BulkImportRecipesDialog from "@/components/recipes/BulkImportRecipesDialog";
import AIRecipeDialog from "@/components/recipes/AIRecipeDialog";
import URLImportDialog from "@/components/recipes/URLImportDialog";
import ImageImportDialog from "@/components/recipes/ImageImportDialog";
import DuplicateRecipesDialog from "@/components/recipes/DuplicateRecipesDialog";
import RecipeCategoriesDialog from "@/components/recipes/RecipeCategoriesDialog";
import RecipeFormDialog from "@/components/recipes/RecipeFormDialog";
import PdfImportDialog from "@/components/recipes/PdfImportDialog";
import AutoFixImagesDialog from "@/components/recipes/AutoFixImagesDialog";
import BulkActionsBar from "@/components/recipes/BulkActionsBar";
import AddToPlanDialog from "@/components/AddToPlanDialog";
import { hostToSourceLabel } from "@/lib/recipeUtils";
import { toast } from "sonner";

export default function Recipes({ servings }) {
  const navigate = useNavigate();
  const { user, activeBoat, loadActiveBoat } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [boatNames, setBoatNames] = useState({});
  const [autoCategorizing, setAutoCategorizing] = useState(false);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [duplicateStats, setDuplicateStats] = useState({ total_recipes: 0, total_duplicates: 0 });
  const [selectedForDeletion, setSelectedForDeletion] = useState({}); // { recipeId: bool }
  const [deletingDupes, setDeletingDupes] = useState(false);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]); // AND-filter på tags
  const [selectedSource, setSelectedSource] = useState("all"); // kilde-filter (domene)
  const [tagInput, setTagInput] = useState("");
  const [editingTagsId, setEditingTagsId] = useState(null);
  const [suggestingTagsId, setSuggestingTagsId] = useState(null);
  const [tagSuggestions, setTagSuggestions] = useState({}); // recipeId → [tags]
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  // Shared helper: after importing/generating a recipe, ask backend for tag suggestions
  const fetchTagSuggestionsFor = async (recipeId) => {
    if (!recipeId) return;
    try {
      const tagRes = await api.suggestRecipeTags(recipeId);
      const tags = tagRes.data?.tags || [];
      if (tags.length > 0) setTagSuggestions((prev) => ({ ...prev, [recipeId]: tags }));
    } catch (err) {
      console.info("tag-forslag feilet:", err?.message);
    }
  };
  const onRecipeAdded = async (newId) => {
    await loadRecipes();
    fetchTagSuggestionsFor(newId);
  };
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [urlOpen, setUrlOpen] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  // Multi-select for bulk move (change category / add to meal plan)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Auto-fix images bulk
  const [autoFixOpen, setAutoFixOpen] = useState(false);
  const [generatingImageId, setGeneratingImageId] = useState(null);
  const [addToPlanRecipe, setAddToPlanRecipe] = useState(null);
  const [recipeSort, setRecipeSort] = useState("default"); // "default" | "alpha"
  const [showTags, setShowTags] = useState(() => localStorage.getItem("pb-recipes-show-tags") !== "0");
  const toggleShowTags = () => {
    setShowTags((v) => {
      const next = !v;
      localStorage.setItem("pb-recipes-show-tags", next ? "1" : "0");
      return next;
    });
  };
  // Visibility of the secondary filter rows ("Tagger:" + "Kilder:"). Hidden by
  // default on mobile so the listing isn't pushed too far down.
  const [showFilters, setShowFilters] = useState(() => localStorage.getItem("pb-recipes-show-filters") !== "0");
  const toggleShowFilters = () => {
    setShowFilters((v) => {
      const next = !v;
      localStorage.setItem("pb-recipes-show-filters", next ? "1" : "0");
      return next;
    });
  };
  const [recipeServings, setRecipeServings] = useState({});
  const [nutritionLoading, setNutritionLoading] = useState({});

  const [allRecipes, setAllRecipes] = useState([]);

  const loadRecipes = useCallback(async () => {
    try {
      const query = filter === "baking" ? true : filter === "meals" ? false : undefined;
      const res = await api.getRecipes(query, searchQuery || undefined);
      setAllRecipes(res.data || []);
    } catch { toast.error("Kunne ikke laste oppskrifter"); }
  }, [filter, searchQuery]);

  // Klientsidig filter – unngår network-roundtrip med tunge base64-bilder
  useEffect(() => {
    let filtered = allRecipes;
    if (categoryFilter && categoryFilter !== "all") {
      filtered = filtered.filter((r) => {
        const cat = r.category || (r.is_baking ? "Bakst" : r.meal_type);
        return cat === categoryFilter;
      });
    }
    if (favoritesOnly) {
      filtered = filtered.filter((r) => r.is_favorite);
    }
    // Tag AND-filter: oppskriften må ha ALLE valgte tags
    if (selectedTags.length > 0) {
      const selLower = selectedTags.map((t) => t.toLowerCase());
      filtered = filtered.filter((r) => {
        const tags = (r.tags || []).map((t) => t.toLowerCase());
        return selLower.every((t) => tags.includes(t));
      });
    }
    // Kilde-filter (basert på source_url sitt hostname)
    if (selectedSource && selectedSource !== "all") {
      filtered = filtered.filter((r) => hostToSourceLabel(r.source_url) === selectedSource);
    }
    if (recipeSort === "alpha") {
      filtered = [...filtered].sort((a, b) =>
        (a.title || "").localeCompare(b.title || "", "nb", { sensitivity: "base" })
      );
    }
    setRecipes(filtered);
  }, [allRecipes, categoryFilter, favoritesOnly, selectedTags, selectedSource, recipeSort]);

  useEffect(() => { loadRecipes(); }, [loadRecipes]);

  const getRecipeServings = (recipe) => recipeServings[recipe.id] || servings;
  const adjustServings = (recipeId, delta) => {
    const current = recipeServings[recipeId] || servings;
    setRecipeServings((prev) => ({ ...prev, [recipeId]: Math.max(1, current + delta) }));
  };

  const calcNutrition = async (recipeId) => {
    setNutritionLoading((p) => ({ ...p, [recipeId]: true }));
    try {
      const res = await api.calculateNutrition(recipeId);
      setRecipes((prev) => prev.map((r) => r.id === recipeId ? { ...r, nutrition: res.data } : r));
      toast.success("Næringsinnhold beregnet!");
    } catch { toast.error("Feil ved beregning"); }
    finally { setNutritionLoading((p) => ({ ...p, [recipeId]: false })); }
  };

  // Open form for create or edit
  const openCreate = () => { setEditingRecipe(null); setFormOpen(true); };
  const openEdit = (recipe) => { setEditingRecipe(recipe); setFormOpen(true); };

  const handleGenerateImage = async (recipe) => {
    if (generatingImageId) return;
    setGeneratingImageId(recipe.id);
    toast.info(`Genererer AI-bilde for "${recipe.title}" — dette tar 15-30 sek...`);
    try {
      await api.regenerateRecipeImage(recipe.id);
      toast.success(`Bilde generert for ${recipe.title}`);
      loadRecipes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke generere bilde");
    } finally {
      setGeneratingImageId(null);
    }
  };

  const handleDelete = async (id) => {
    try { await api.deleteRecipe(id); setRecipes((prev) => prev.filter((r) => r.id !== id)); toast.success("Oppskrift slettet"); }
    catch { toast.error("Feil ved sletting"); }
  };

  const handleToggleFavorite = async (id) => {
    try {
      const res = await api.toggleFavorite(id);
      setRecipes((prev) => prev.map((r) => r.id === id ? { ...r, is_favorite: res.data.is_favorite } : r));
    } catch { toast.error("Feil ved favorittmerking"); }
  };

  // Last inn båt-navn for kilde-markør
  useEffect(() => {
    (async () => {
      try {
        const res = await api.getRecipeBoatNames();
        setBoatNames(res.data || {});
      } catch (err) { console.info("Båt-navn feilet:", err?.message); }
    })();
  }, [recipes.length]);

  // AI-sorter oppskrifter etter type
  const handleAutoCategorize = async () => {
    if (autoCategorizing) return;
    setAutoCategorizing(true);
    toast.loading("AI sorterer oppskriftene...", { id: "autocat" });
    try {
      const res = await api.autoCategorizeRecipes();
      const { updated, total, by_category } = res.data;
      if (updated === 0) {
        toast.success(`Alle ${total} oppskrifter har allerede riktig kategori`, { id: "autocat" });
      } else {
        const breakdown = Object.entries(by_category || {}).map(([k, v]) => `${k}: ${v}`).join(", ");
        toast.success(`${updated} av ${total} oppskrifter oppdatert (${breakdown})`, { id: "autocat", duration: 5000 });
      }
      await loadRecipes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved sortering", { id: "autocat" });
    } finally {
      setAutoCategorizing(false);
    }
  };

  // AI-duplikatdeteksjon
  const handleFindDuplicates = async () => {
    if (duplicatesLoading) return;
    setDuplicatesLoading(true);
    setDuplicatesOpen(true);
    toast.loading("AI analyserer oppskrifter for duplikat…", { id: "dupes" });
    try {
      const res = await api.findRecipeDuplicates();
      const { groups = [], total_recipes = 0, total_duplicates = 0 } = res.data;
      setDuplicateGroups(groups);
      setDuplicateStats({ total_recipes, total_duplicates });
      // Førehandsvel alle føreslegne duplikat for sletting (brukaren kan skru av)
      const preselect = {};
      groups.forEach((g) => g.duplicate_ids.forEach((id) => { preselect[id] = true; }));
      setSelectedForDeletion(preselect);
      if (total_duplicates === 0) {
        toast.success(`Ingen duplikat funne blant ${total_recipes} oppskrifter 🎉`, { id: "dupes" });
      } else {
        toast.success(`Fann ${total_duplicates} duplikat i ${groups.length} grupper`, { id: "dupes", duration: 4000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI-analyse feila", { id: "dupes" });
      setDuplicatesOpen(false);
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const handleDeleteSelectedDupes = async () => {
    const idsToDelete = Object.entries(selectedForDeletion).filter(([, v]) => v).map(([k]) => k);
    if (!idsToDelete.length) { toast.info("Ingen valt for sletting"); return; }
    if (!window.confirm(`Slett ${idsToDelete.length} oppskrift${idsToDelete.length > 1 ? "er" : ""}? Dette kan ikkje angrast.`)) return;
    setDeletingDupes(true);
    try {
      const res = await api.deleteRecipeDuplicates(idsToDelete);
      const deleted = res.data?.deleted || 0;
      toast.success(`Sletta ${deleted} oppskrift${deleted !== 1 ? "er" : ""}`);
      setDuplicatesOpen(false);
      setDuplicateGroups([]);
      setSelectedForDeletion({});
      await loadRecipes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved sletting");
    } finally {
      setDeletingDupes(false);
    }
  };

  // Auto-fix all missing images — bruker HELE samlingen, ikke den filtrerte
  const recipesMissingImage = allRecipes.filter((r) => !r.has_image);

  const handleImageGenerated = (recipeId, hasImage) => {
    setAllRecipes((prev) => prev.map((r) => (r.id === recipeId ? { ...r, has_image: hasImage } : r)));
  };

  const scaleAmount = (amount, recipeServings, targetServings) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    const scaled = (num / recipeServings) * targetServings;
    return scaled % 1 === 0 ? scaled.toString() : scaled.toFixed(1);
  };

  const images = [
    "https://images.unsplash.com/photo-1473093295043-cdd812d0e601?auto=format&fit=crop&q=80&w=400",
    "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80&w=400",
    "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&q=80&w=400",
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="recipes-page">
      <div className="flex items-start justify-between gap-3 mb-8">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Oppskrifter</h1>
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            <span className="text-xs sm:text-sm text-stone-500">{recipes.length} i samlingen</span>
            {/* Compact icon-tool row sitting under the title */}
            <span className="text-stone-300">·</span>
            <button
              onClick={() => setUrlOpen(true)}
              className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-stone-100 text-stone-500 hover:text-navy-800 transition-colors"
              data-testid="import-url-btn"
              title="Importer frå nett-URL"
              aria-label="Importer frå nett"
            >
              <Globe className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setImageOpen(true)}
              className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-stone-100 text-stone-500 hover:text-navy-800 transition-colors"
              data-testid="import-image-btn"
              title="Importer oppskrift fra bilde(r)"
              aria-label="Importer frå bilde"
            >
              <Camera className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setPdfOpen(true)}
              className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-stone-100 text-stone-500 hover:text-navy-800 transition-colors"
              data-testid="import-pdf-btn"
              title="Importer PDF-fil"
              aria-label="Importer PDF"
            >
              <FileUp className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setAiOpen(true)}
              className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-orange-50 hover:bg-orange-100 text-orange-700 transition-colors"
              data-testid="generate-recipe-button"
              title="AI lagar oppskrift"
              aria-label="AI Oppskrift"
            >
              <Sparkles className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => { setSelectMode((v) => !v); setSelectedIds(new Set()); }}
              className={`inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors ${selectMode ? "bg-navy-800 text-white hover:bg-navy-900" : "hover:bg-stone-100 text-stone-500 hover:text-navy-800"}`}
              data-testid="select-mode-toggle"
              title={selectMode ? "Avbryt valg" : "Velg flere oppskrifter"}
              aria-label="Velg flere"
            >
              <UtensilsCrossed className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={handleAutoCategorize}
              disabled={autoCategorizing}
              className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-stone-100 text-stone-500 hover:text-navy-800 transition-colors disabled:opacity-40"
              data-testid="auto-categorize-btn"
              title="AI-sorter: kategoriser alle oppskrifter automatisk"
              aria-label="AI-sorter"
            >
              <Zap className={`h-3.5 w-3.5 ${autoCategorizing ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={handleFindDuplicates}
              disabled={duplicatesLoading}
              className="inline-flex items-center justify-center gap-1 h-7 px-2 rounded-md bg-violet-50 hover:bg-violet-100 text-violet-700 transition-colors text-[11px] font-semibold disabled:opacity-40"
              data-testid="find-duplicates-btn"
              title="AI: finn og fjern duplikat-oppskrifter"
            >
              <Sparkles className={`h-3.5 w-3.5 ${duplicatesLoading ? "animate-spin" : ""}`} />
              {duplicatesLoading ? "Analyserer…" : "Sjekk duplikat"}
            </button>
            {recipesMissingImage.length > 0 && (
              <button
                onClick={() => setAutoFixOpen(true)}
                className="inline-flex items-center justify-center gap-1 h-7 px-2 rounded-md bg-sky-50 hover:bg-sky-100 text-sky-700 transition-colors text-[11px] font-semibold"
                data-testid="auto-fix-images-btn"
                title="Generer AI-bilder for alle oppskrifter som manglar"
              >
                <ImageIcon className="h-3.5 w-3.5" /> Auto-fiks ({recipesMissingImage.length})
              </button>
            )}
            {user?.role === "admin" && (
              <button
                onClick={() => { setBulkImportOpen(true); }}
                className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition-colors"
                data-testid="bulk-import-recipes-btn"
                title="Last ned ein kuratert samling norske oppskrifter (admin)"
                aria-label="Last ned fleire (admin)"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        {/* Primary action — always visible & easy to hit */}
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center rounded-md bg-navy-800 px-3 sm:px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors gap-2 flex-shrink-0"
          data-testid="create-recipe-btn"
        >
          <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Ny oppskrift</span>
        </button>
      </div>

      {/* Search + Filter */}
      <div className="space-y-3 mb-6">
        {/* Dedicated full-width search bar — always visible at the top */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Søk i oppskrifter (tittel, ingrediens, kategori, tag) …"
            className="w-full rounded-lg border border-stone-200 bg-white pl-10 pr-10 py-3 text-base focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none shadow-sm"
            data-testid="recipe-search-input-top"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-md text-stone-400 hover:text-stone-700 hover:bg-stone-100"
              title="Tøm søk"
              data-testid="recipe-search-clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {(() => {
          const hasActiveFilter =
            searchQuery.trim() !== "" ||
            filter !== "all" ||
            categoryFilter !== "all" ||
            favoritesOnly ||
            selectedTags.length > 0 ||
            selectedSource !== "all";
          const resetAll = () => {
            setSearchQuery("");
            setFilter("all");
            setCategoryFilter("all");
            setFavoritesOnly(false);
            setSelectedTags([]);
            setSelectedSource("all");
          };
          if (!hasActiveFilter) return null;
          return (
            <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm" data-testid="active-filter-banner">
              <span className="text-amber-900">
                Viser <strong>{recipes.length}</strong> av <strong>{allRecipes.length}</strong> oppskrifter
              </span>
              <span className="text-amber-700">— ett eller flere filtre er aktive</span>
              <button
                onClick={resetAll}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-white border border-amber-300 px-2.5 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
                data-testid="reset-filters-btn"
              >
                <X className="h-3.5 w-3.5" /> Nullstill filtre
              </button>
            </div>
          );
        })()}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex gap-1.5 sm:gap-2 flex-wrap items-center">
            {[{ v: "all", l: "Alle" }, { v: "meals", l: "Måltider" }, { v: "baking", l: "Baking" }].map((f) => (
              <button key={f.v} onClick={() => { setFilter(f.v); setCategoryFilter("all"); }} className={`px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors ${filter === f.v ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`} data-testid={`filter-${f.v}`}>{f.l}</button>
            ))}
            <button onClick={() => setFavoritesOnly(!favoritesOnly)} className={`p-2 sm:px-4 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors flex items-center gap-1.5 ${favoritesOnly ? "bg-red-500 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`} data-testid="filter-favorites" title="Favorittar">
              <Heart className={`h-3.5 w-3.5 ${favoritesOnly ? "fill-current" : ""}`} /> <span className="hidden sm:inline">Favoritter</span>
            </button>
            <button
              onClick={() => setRecipeSort(recipeSort === "alpha" ? "default" : "alpha")}
              className={`px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors flex items-center gap-1.5 ${recipeSort === "alpha" ? "bg-navy-800 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}
              data-testid="sort-alpha"
              title="Sorter alfabetisk A-Å"
            >
              A-Å
            </button>
            <button
              onClick={toggleShowFilters}
              className={`p-2 sm:px-4 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors flex items-center gap-1.5 ${showFilters ? "bg-sky-50 text-sky-700 border border-sky-200" : "bg-stone-100 text-stone-500 hover:bg-stone-200 border border-transparent"}`}
              data-testid="toggle-filters"
              title={showFilters ? "Skjul tagger og kjelder" : "Vis tagger og kjelder"}
              aria-pressed={showFilters}
            >
              <Filter className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{showFilters ? "Skjul filter" : "Vis filter"}</span>
            </button>
            <button
              onClick={toggleShowTags}
              className={`p-2 sm:px-4 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors flex items-center gap-1.5 ${showTags ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-stone-100 text-stone-500 hover:bg-stone-200 border border-transparent"}`}
              data-testid="toggle-tags"
              title={showTags ? "Skjul tagger på oppskrifts­korta" : "Vis tagger på oppskrifts­korta"}
              aria-pressed={showTags}
            >
              {showTags ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{showTags ? "Skjul tags" : "Vis tags"}</span>
            </button>
          </div>
        </div>
        {/* Category tab bar — wraps to next line, max 5-ish per row on desktop */}
        <div className="border-b border-stone-200 -mb-2 pb-1" data-testid="category-tabs">
          <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
            {(() => {
              const counts = allRecipes.reduce((acc, r) => {
                const key = r.category || (r.is_baking ? "Bakst" : r.meal_type || "Middag");
                acc[key] = (acc[key] || 0) + 1;
                return acc;
              }, {});
              const customCats = activeBoat?.recipe_categories || [];
              const builtIns = [
                { v: "Middag",        l: "Middag" },
                { v: "Frokost",       l: "Frokost" },
                { v: "Bakst",         l: "Bakst" },
                { v: "Kjøtt",         l: "Kjøtt" },
                { v: "Fisk",          l: "Fisk" },
                { v: "Pasta",         l: "Pasta" },
                { v: "Suppe",         l: "Suppe" },
                { v: "Dessert",       l: "Dessert" },
                { v: "Salat",         l: "Salat" },
                { v: "Saus",          l: "Saus" },
                { v: "Tradisjonsmat", l: "Tradisjon" },
                { v: "Annet",         l: "Annet" },
              ];
              const builtInIds = new Set(builtIns.map((b) => b.v.toLowerCase()));
              const tabs = [{ v: "all", l: "Alle", count: allRecipes.length }]
                .concat(builtIns.map((b) => ({ ...b, count: counts[b.v] || 0 })))
                .concat(customCats
                  .filter((c) => !builtInIds.has((c.name || "").toLowerCase()))
                  .map((c) => ({ v: c.name, l: c.name, count: counts[c.name] || 0 }))
                );
              const customCatNames = new Set(customCats.map((c) => (c.name || "").toLowerCase()));
              return (
                <>
                  {tabs
                    .filter((c) => {
                      if (c.v === "all") return true;
                      if (customCatNames.has((c.v || "").toLowerCase())) return true;
                      return (c.count || 0) > 0;
                    })
                    .map((c) => {
                      const active = categoryFilter === c.v;
                      return (
                        <button
                          key={c.v}
                          onClick={() => setCategoryFilter(c.v)}
                          className={`relative inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap border-b-2 -mb-px ${
                            active ? "text-navy-800 border-navy-800" : "text-stone-500 border-transparent hover:text-stone-800 hover:border-stone-300"
                          }`}
                          data-testid={`category-tab-${c.v}`}
                        >
                          {c.l}
                          <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${active ? "bg-navy-100 text-navy-800" : "bg-stone-100 text-stone-400"}`}>{c.count}</span>
                        </button>
                      );
                    })}
                  {activeBoat?.id && (
                    <button
                      onClick={() => setCategoriesOpen(true)}
                      className="inline-flex items-center gap-1 px-2 sm:px-3 py-2 text-xs text-stone-400 hover:text-navy-800 transition-colors border-b-2 border-transparent hover:border-stone-300 -mb-px whitespace-nowrap"
                      data-testid="edit-recipe-categories-btn"
                      title="Lag eller endre egne kategorier"
                    >
                      <span className="text-base leading-none">+</span> <span className="hidden sm:inline">Egen</span>
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </div>
        {/* Tags multi-select filter (AND) */}
        {showFilters && (() => {
          const tagCounts = allRecipes.reduce((acc, r) => {
            (r.tags || []).forEach((t) => {
              const k = (t || "").trim();
              if (!k) return;
              acc[k] = (acc[k] || 0) + 1;
            });
            return acc;
          }, {});
          const allTags = Object.entries(tagCounts).sort((a, b) => b[1] - a[1]);
          if (allTags.length === 0 && selectedTags.length === 0) return null;
          return (
            <div className="flex gap-1.5 flex-wrap items-center" data-testid="tag-filters">
              <span className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mr-1">Tagger:</span>
              {allTags.map(([t, count]) => {
                const active = selectedTags.includes(t);
                return (
                  <button
                    key={t}
                    onClick={() => setSelectedTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t])}
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors ${active ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
                    data-testid={`tag-chip-${t}`}
                  >
                    #{t}
                    <span className={`text-[9px] ${active ? "text-white/80" : "text-emerald-400"}`}>({count})</span>
                  </button>
                );
              })}
              {selectedTags.length > 0 && (
                <button
                  onClick={() => setSelectedTags([])}
                  className="text-[10px] text-stone-400 hover:text-red-500 font-semibold underline ml-1"
                  data-testid="clear-tags-btn"
                >Nullstill</button>
              )}
            </div>
          );
        })()}
        {/* Source filter (kilde) — shown when any recipe has a source_url */}
        {showFilters && (() => {
          const sourceCounts = allRecipes.reduce((acc, r) => {
            const label = hostToSourceLabel(r.source_url);
            if (!label) return acc;
            acc[label] = (acc[label] || 0) + 1;
            return acc;
          }, {});
          const labels = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]);
          if (labels.length === 0) return null;
          return (
            <div className="flex gap-1.5 flex-wrap items-center" data-testid="source-filters">
              <span className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mr-1">Kilder:</span>
              <button
                onClick={() => setSelectedSource("all")}
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors ${selectedSource === "all" ? "bg-sky-600 text-white" : "bg-sky-50 text-sky-700 hover:bg-sky-100"}`}
                data-testid="source-chip-all"
              >
                Alle
                <span className={`text-[9px] ${selectedSource === "all" ? "text-white/80" : "text-sky-400"}`}>({allRecipes.length})</span>
              </button>
              {labels.map(([label, count]) => {
                const active = selectedSource === label;
                return (
                  <button
                    key={label}
                    onClick={() => setSelectedSource(active ? "all" : label)}
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors ${active ? "bg-sky-600 text-white" : "bg-sky-50 text-sky-700 hover:bg-sky-100"}`}
                    data-testid={`source-chip-${label}`}
                  >
                    {label}
                    <span className={`text-[9px] ${active ? "text-white/80" : "text-sky-400"}`}>({count})</span>
                  </button>
                );
              })}
            </div>
          );
        })()}
      </div>

      {recipes.length === 0 ? (
        <div className="text-center py-20" data-testid="no-recipes">
          <BookOpen className="h-12 w-12 mx-auto text-stone-300 mb-4" />
          <h3 className="text-lg font-medium text-stone-600" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ingen oppskrifter ennå</h3>
          <p className="text-sm text-stone-400 mt-1">Opprett en ny, generer med AI, eller importer fra PDF</p>
        </div>
      ) : (
        <div className="space-y-6">
          {(() => {
            const renderCard = (recipe, idx) => {
            const currentServings = getRecipeServings(recipe);
            const isSelected = selectedIds.has(recipe.id);
            return (
              <div key={recipe.id} className={`bg-white border rounded-xl overflow-hidden animate-fade-in transition-all ${isSelected ? "border-navy-800 ring-2 ring-navy-400" : "border-stone-100"}`} style={{ animationDelay: `${idx * 0.05}s` }} data-testid={`recipe-card-${recipe.id}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("application/recipe-id", recipe.id);
                  e.dataTransfer.setData("application/recipe-title", recipe.title);
                  e.dataTransfer.effectAllowed = "copy";
                }}
              >
                <div className="flex">
                  {selectMode && (
                    <div className="flex items-center justify-center px-3 bg-stone-50 border-r border-stone-100">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(recipe.id)) next.delete(recipe.id);
                            else next.add(recipe.id);
                            return next;
                          });
                        }}
                        className="h-5 w-5 rounded border-stone-300 text-navy-800 focus:ring-navy-800 cursor-pointer"
                        data-testid={`select-recipe-${recipe.id}`}
                      />
                    </div>
                  )}
                  <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 overflow-hidden cursor-pointer" onClick={() => navigate(`/recipes/${recipe.id}`)}><img src={recipe.has_image ? `${process.env.REACT_APP_BACKEND_URL}/api/recipes/${recipe.id}/image` : images[idx % images.length]} alt={recipe.title} loading="lazy" decoding="async" className="w-full h-full object-cover" /></div>
                  <div className="flex-1 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 flex-wrap">
                        <h3 className="text-base sm:text-lg font-semibold text-stone-800 hover:text-navy-800 cursor-pointer transition-colors truncate" style={{ fontFamily: "'Cormorant Garamond', serif" }} onClick={() => navigate(`/recipes/${recipe.id}`)} data-testid={`recipe-title-link-${recipe.id}`}>{recipe.title}</h3>
                        <span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-[10px] sm:text-xs font-medium text-stone-600 flex-shrink-0">{recipe.category || (recipe.is_baking ? "Bakst" : recipe.meal_type)}</span>
                        {recipe.boat_id && boatNames[recipe.boat_id] && (
                          <span className="hidden sm:inline-flex items-center rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-2 py-0.5 text-[10px] font-medium" title={`Opprettet av ${boatNames[recipe.boat_id]}`} data-testid={`recipe-source-${recipe.id}`}>
                            Fra {boatNames[recipe.boat_id]}
                          </span>
                        )}
                        {hostToSourceLabel(recipe.source_url) && (
                          <a
                            href={recipe.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-2 py-0.5 text-[10px] font-medium hover:bg-sky-100 hover:underline"
                            title={`Fra ${hostToSourceLabel(recipe.source_url)} — klikk for å åpne original`}
                            data-testid={`recipe-url-source-${recipe.id}`}
                          >
                            🔗 {hostToSourceLabel(recipe.source_url)}
                          </a>
                        )}
                      </div>
                      {recipe.description && <p className="hidden sm:block text-sm text-stone-500 mt-0.5 truncate">{recipe.description}</p>}
                      <div className="flex gap-2 sm:gap-3 mt-1 text-[10px] sm:text-xs text-stone-400 flex-wrap">
                        {recipe.prep_time && <span>Prep: {recipe.prep_time}</span>}
                        {recipe.cook_time && <span className="hidden sm:inline">Tilbered: {recipe.cook_time}</span>}
                        <span>{recipe.ingredients?.length || 0} ingred.</span>
                        {(recipe.side_dishes?.length || 0) > 0 && <span className="hidden sm:inline">{recipe.side_dishes.length} tilbehør</span>}
                      </div>
                      {/* Tags row (kan skjules via toggle øvst) */}
                      {showTags && (<>
                      <div className="flex items-center gap-1 mt-1.5 flex-wrap" data-testid={`recipe-tags-${recipe.id}`}>
                        {(recipe.tags || []).map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-medium group/tag"
                            data-testid={`recipe-tag-${recipe.id}-${t}`}
                          >
                            #{t}
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const newTags = (recipe.tags || []).filter((x) => x !== t);
                                  await api.updateRecipe(recipe.id, { tags: newTags });
                                  setAllRecipes((prev) => prev.map((r) => r.id === recipe.id ? { ...r, tags: newTags } : r));
                                } catch { toast.error("Kunne ikke fjerne tag"); }
                              }}
                              className="opacity-0 group-hover/tag:opacity-100 text-emerald-500 hover:text-red-500 transition-opacity"
                              data-testid={`remove-tag-${recipe.id}-${t}`}
                              aria-label={`Fjern tag ${t}`}
                            >×</button>
                          </span>
                        ))}
                        {editingTagsId === recipe.id ? (() => {
                          // Robust tag-add flow that works on desktop and mobile.
                          // Mobile keyboards don't always send a proper `Enter` keydown; they
                          // fire `submit` on the enclosing form or `blur` on the input. We
                          // handle all three: (1) form submit, (2) onKeyDown Enter,
                          // (3) explicit "Legg til"-button, and (4) save-on-blur if non-empty.
                          const saveTag = async () => {
                            const t = (tagInput || "").trim();
                            if (!t) return;
                            if ((recipe.tags || []).map((x) => x.toLowerCase()).includes(t.toLowerCase())) {
                              setTagInput("");
                              return;
                            }
                            const newTags = [...(recipe.tags || []), t];
                            try {
                              await api.updateRecipe(recipe.id, { tags: newTags });
                              setAllRecipes((prev) => prev.map((r) => r.id === recipe.id ? { ...r, tags: newTags } : r));
                              setTagInput("");
                            } catch {
                              toast.error("Kunne ikke legge til tag");
                            }
                          };
                          return (
                            <form
                              onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); saveTag(); }}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1"
                              data-testid={`tag-form-${recipe.id}`}
                            >
                              <input
                                autoFocus
                                type="text"
                                enterKeyHint="done"
                                autoComplete="off"
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    setEditingTagsId(null);
                                    setTagInput("");
                                  }
                                }}
                                onBlur={() => {
                                  // Give the "Legg til"-button a moment to fire its onClick
                                  // before we auto-save + close (prevents double-save).
                                  setTimeout(() => {
                                    if ((tagInput || "").trim()) { saveTag(); }
                                    setEditingTagsId(null);
                                    setTagInput("");
                                  }, 150);
                                }}
                                placeholder="tagnavn"
                                className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] text-emerald-800 outline-none focus:border-emerald-500 w-24"
                                data-testid={`tag-input-${recipe.id}`}
                              />
                              <button
                                type="submit"
                                onMouseDown={(e) => e.preventDefault() /* keep focus so blur doesn't race */}
                                className="rounded-full bg-emerald-600 text-white px-2 py-0.5 text-[10px] font-semibold hover:bg-emerald-700"
                                data-testid={`save-tag-${recipe.id}`}
                              >
                                Legg til
                              </button>
                            </form>
                          );
                        })() : (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); setEditingTagsId(recipe.id); setTagInput(""); }}
                              className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-stone-200 text-stone-400 hover:text-emerald-600 hover:border-emerald-300 px-2 py-0.5 text-[10px] font-medium transition-colors"
                              data-testid={`add-tag-${recipe.id}`}
                              title="Legg til tag"
                            >
                              + tag
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (suggestingTagsId === recipe.id) return;
                                setSuggestingTagsId(recipe.id);
                                try {
                                  const res = await api.suggestRecipeTags(recipe.id);
                                  const newTags = res.data?.tags || [];
                                  if (newTags.length === 0) { toast.info("AI fant ingen nye tagger å foreslå"); return; }
                                  setTagSuggestions((prev) => ({ ...prev, [recipe.id]: newTags }));
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || "AI-feil");
                                } finally { setSuggestingTagsId(null); }
                              }}
                              className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-violet-200 text-violet-500 hover:bg-violet-50 px-2 py-0.5 text-[10px] font-medium transition-colors"
                              data-testid={`suggest-tags-${recipe.id}`}
                              title="La AI foreslå tagger"
                              disabled={suggestingTagsId === recipe.id}
                            >
                              {suggestingTagsId === recipe.id ? "..." : "✨ AI"}
                            </button>
                          </>
                        )}
                      </div>
                      {/* AI-suggested tags accept row */}
                      {tagSuggestions[recipe.id] && tagSuggestions[recipe.id].length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5 flex-wrap p-1.5 rounded-md bg-violet-50 border border-violet-100" data-testid={`suggestions-${recipe.id}`}>
                          <span className="text-[10px] uppercase tracking-wider text-violet-700 font-bold">AI foreslår:</span>
                          {tagSuggestions[recipe.id].map((t) => (
                            <button
                              key={t}
                              onClick={async (e) => {
                                e.stopPropagation();
                                const newTags = [...(recipe.tags || []), t];
                                try {
                                  await api.updateRecipe(recipe.id, { tags: newTags });
                                  setAllRecipes((prev) => prev.map((r) => r.id === recipe.id ? { ...r, tags: newTags } : r));
                                  setTagSuggestions((prev) => ({ ...prev, [recipe.id]: (prev[recipe.id] || []).filter((x) => x !== t) }));
                                } catch { toast.error("Feil"); }
                              }}
                              className="inline-flex items-center gap-0.5 rounded-full bg-white border border-violet-200 text-violet-700 hover:bg-violet-600 hover:text-white px-2 py-0.5 text-[10px] font-medium transition-colors"
                              data-testid={`accept-tag-${recipe.id}-${t}`}
                            >
                              + #{t}
                            </button>
                          ))}
                          <button
                            onClick={(e) => { e.stopPropagation(); setTagSuggestions((prev) => ({ ...prev, [recipe.id]: [] })); }}
                            className="text-[10px] text-stone-400 hover:text-red-500 ml-auto font-semibold"
                            data-testid={`dismiss-suggestions-${recipe.id}`}
                          >Lukk</button>
                        </div>
                      )}
                      </>)}
                    </div>
                    <div className="flex items-center gap-0.5 sm:gap-1 sm:ml-4 flex-shrink-0 self-end sm:self-auto">
                      {!recipe.has_image && (
                        <button
                          onClick={() => handleGenerateImage(recipe)}
                          disabled={generatingImageId === recipe.id}
                          className="p-1.5 sm:p-2 rounded-md text-seawater-600 hover:bg-seawater-50 transition-colors disabled:opacity-50"
                          title="Generer AI-bilde (gratis, Nano Banana)"
                          data-testid={`gen-image-${recipe.id}`}
                        >
                          <Sparkles className={`h-4 w-4 ${generatingImageId === recipe.id ? "animate-pulse" : ""}`} />
                        </button>
                      )}
                      <button onClick={() => setAddToPlanRecipe(recipe)} className="p-1.5 sm:p-2 rounded-md text-navy-700 hover:bg-navy-50 transition-colors" title="Legg til i ukeplan" data-testid={`add-to-plan-${recipe.id}`}>
                        <CalendarPlus className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleToggleFavorite(recipe.id)} className={`p-1.5 sm:p-2 rounded-md transition-colors ${recipe.is_favorite ? "text-red-500 hover:bg-red-50" : "text-stone-400 hover:bg-stone-100 hover:text-red-400"}`} data-testid={`fav-recipe-${recipe.id}`}><Heart className={`h-4 w-4 ${recipe.is_favorite ? "fill-current" : ""}`} /></button>
                      <button onClick={() => openEdit(recipe)} className="p-1.5 sm:p-2 hover:bg-stone-100 rounded-md text-stone-400 hover:text-navy-800 transition-colors" data-testid={`edit-recipe-${recipe.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => setExpandedId(expandedId === recipe.id ? null : recipe.id)} className="p-1.5 sm:p-2 hover:bg-stone-100 rounded-md text-stone-400 hover:text-stone-600 transition-colors" data-testid={`toggle-recipe-${recipe.id}`}>{expandedId === recipe.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
                      <button onClick={() => handleDelete(recipe.id)} className="p-1.5 sm:p-2 hover:bg-red-50 rounded-md text-stone-400 hover:text-red-500 transition-colors" data-testid={`delete-recipe-${recipe.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>

                {expandedId === recipe.id && (
                  <div className="border-t border-stone-100 p-4 bg-stone-50/50" data-testid={`recipe-details-${recipe.id}`}>
                    <div className="flex items-center gap-3 mb-4 p-3 bg-white rounded-lg border border-stone-200 w-fit" data-testid={`servings-adjuster-${recipe.id}`}>
                      <Users className="h-4 w-4 text-stone-400" /><span className="text-sm font-medium text-stone-600">Personer:</span>
                      <button onClick={() => adjustServings(recipe.id, -1)} className="w-7 h-7 rounded-md bg-stone-100 flex items-center justify-center text-stone-600 hover:bg-stone-200 transition-colors" data-testid={`servings-minus-${recipe.id}`}><Minus className="h-3.5 w-3.5" /></button>
                      <span className="text-sm font-bold text-stone-800 w-6 text-center" data-testid={`servings-count-${recipe.id}`}>{currentServings}</span>
                      <button onClick={() => adjustServings(recipe.id, 1)} className="w-7 h-7 rounded-md bg-stone-100 flex items-center justify-center text-stone-600 hover:bg-stone-200 transition-colors" data-testid={`servings-plus-${recipe.id}`}><Plus className="h-3.5 w-3.5" /></button>
                      <span className="text-xs text-stone-400">(Oppskrift: {recipe.servings})</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3">Ingredienser</h4>
                        <ul className="space-y-1.5">
                          {recipe.ingredients?.map((ing, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-stone-600"><span className="w-1.5 h-1.5 rounded-full bg-seawater-600 flex-shrink-0" /><span className="font-medium">{scaleAmount(ing.amount, recipe.servings, currentServings)} {ing.unit}</span><span>{ing.name}</span></li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3">Fremgangsmåte</h4>
                        <ol className="space-y-2">
                          {recipe.instructions?.map((step, i) => (
                            <li key={i} className="flex gap-3 text-sm text-stone-600"><span className="w-6 h-6 rounded-full bg-navy-50 text-navy-800 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span><span className="pt-0.5">{step}</span></li>
                          ))}
                        </ol>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <UtensilsCrossed className="h-3.5 w-3.5" />Tilbehør
                        </h4>
                        {(recipe.side_dishes?.length || 0) === 0 ? (
                          <p className="text-sm text-stone-400 italic">Ingen tilbehør lagt til</p>
                        ) : (
                          <ul className="space-y-2">
                            {recipe.side_dishes.map((sd, i) => (
                              <li key={`sd-${i}-${sd.name || ""}`} className="p-2 bg-white rounded-lg border border-stone-100">
                                <span className="text-sm font-medium text-stone-700">{sd.name}</span>
                                {sd.description && <p className="text-xs text-stone-400 mt-0.5">{sd.description}</p>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    {/* Nutrition */}
                    <div className="mt-4 p-3 bg-white rounded-lg border border-stone-200" data-testid={`nutrition-${recipe.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-stone-700 uppercase tracking-wider flex items-center gap-2"><Zap className="h-3.5 w-3.5" />Næringsinnhold per porsjon</h4>
                        <button onClick={() => calcNutrition(recipe.id)} disabled={nutritionLoading[recipe.id]} className="text-xs text-navy-800 hover:text-navy-800 font-medium disabled:opacity-50" data-testid={`calc-nutrition-${recipe.id}`}>
                          {nutritionLoading[recipe.id] ? "Beregner..." : recipe.nutrition ? "Beregn på nytt" : "Beregn med AI"}
                        </button>
                      </div>
                      {recipe.nutrition ? (
                        <div className="grid grid-cols-5 gap-3">
                          {[
                            { label: "Kalorier", value: `${recipe.nutrition.calories}`, unit: "kcal", color: "bg-orange-50 text-orange-700" },
                            { label: "Protein", value: `${recipe.nutrition.protein}`, unit: "g", color: "bg-blue-50 text-blue-700" },
                            { label: "Fett", value: `${recipe.nutrition.fat}`, unit: "g", color: "bg-amber-50 text-amber-700" },
                            { label: "Karbo", value: `${recipe.nutrition.carbs}`, unit: "g", color: "bg-green-50 text-green-700" },
                            { label: "Fiber", value: `${recipe.nutrition.fiber}`, unit: "g", color: "bg-purple-50 text-purple-700" },
                          ].map((n) => (
                            <div key={n.label} className={`rounded-lg p-2 text-center ${n.color}`}>
                              <span className="text-lg font-bold block">{n.value}</span>
                              <span className="text-[10px] uppercase tracking-wider font-medium">{n.unit} {n.label}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-stone-400 italic">Klikk "Beregn med AI" for å estimere næringsinnhold</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          };

          const CAT_ORDER = ["Frokost", "Lunsj", "Middag", "Kjøtt", "Fisk", "Pasta", "Suppe", "Salat", "Saus", "Tradisjonsmat", "Dessert", "Bakst"];
          const getCat = (r) => r.category || (r.is_baking ? "Bakst" : (r.meal_type === "lunch" ? "Lunsj" : r.meal_type === "breakfast" ? "Frokost" : r.meal_type === "dessert" ? "Dessert" : "Middag"));

          if (categoryFilter !== "all") {
            return <div className="space-y-4">{recipes.map((r, i) => renderCard(r, i))}</div>;
          }

          const groups = {};
          recipes.forEach((r) => {
            const c = getCat(r);
            (groups[c] = groups[c] || []).push(r);
          });
          const ordered = [
            ...CAT_ORDER.filter((c) => groups[c]?.length),
            ...Object.keys(groups).filter((c) => !CAT_ORDER.includes(c)).sort(),
          ];
          let idx = 0;
          return ordered.map((cat) => (
            <section key={cat} className="space-y-3" data-testid={`recipe-group-${cat}`}>
              <div className="flex items-baseline gap-3 pt-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {cat}
                </h2>
                <span className="text-xs text-stone-400">{groups[cat].length} oppskrifter</span>
                <div className="flex-1 h-px bg-gradient-to-r from-stone-200 to-transparent" />
              </div>
              <div className="space-y-4">{groups[cat].map((r) => renderCard(r, idx++))}</div>
            </section>
          ));
          })()}
        </div>
      )}

      {/* Create / Edit Recipe Dialog */}
      <RecipeFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editingRecipe={editingRecipe}
        onSaved={loadRecipes}
      />

      {/* AI Dialog */}
      <AIRecipeDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        servings={servings}
        onGenerated={onRecipeAdded}
      />

      {/* PDF Dialog */}
      <PdfImportDialog
        open={pdfOpen}
        onOpenChange={setPdfOpen}
        servings={servings}
        onImported={onRecipeAdded}
      />

      {/* URL Import Dialog */}
      <URLImportDialog
        open={urlOpen}
        onOpenChange={setUrlOpen}
        servings={servings}
        onImported={onRecipeAdded}
      />

      {/* Image Import Dialog (photos → AI vision → recipe) */}
      <ImageImportDialog
        open={imageOpen}
        onOpenChange={setImageOpen}
        servings={servings}
        onImported={onRecipeAdded}
      />

      {/* Custom Recipe Categories Dialog */}
      {activeBoat?.id && (
        <RecipeCategoriesDialog
          open={categoriesOpen}
          onOpenChange={setCategoriesOpen}
          boatId={activeBoat.id}
          initialCategories={activeBoat.recipe_categories || []}
          onSaved={() => { loadActiveBoat?.(); }}
        />
      )}

      {/* Auto-fix images dialog */}
      <AutoFixImagesDialog
        open={autoFixOpen}
        onOpenChange={setAutoFixOpen}
        targets={recipesMissingImage}
        onRecipeImageSet={handleImageGenerated}
      />

      {/* Bulk-import curated popular recipes (admin) */}
      <BulkImportRecipesDialog
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        onImported={loadRecipes}
      />

      {/* Add-to-plan dialog (single-recipe) */}
      <AddToPlanDialog
        open={!!addToPlanRecipe}
        onOpenChange={(o) => { if (!o) setAddToPlanRecipe(null); }}
        recipe={addToPlanRecipe}
      />

      {/* Floating action bar + bulk-move dialogs */}
      {selectMode && (
        <BulkActionsBar
          selectedIds={selectedIds}
          allRecipes={allRecipes}
          onClearSelection={() => { setSelectMode(false); setSelectedIds(new Set()); }}
          onAfterChange={loadRecipes}
        />
      )}

      {/* AI-duplikatsjekk */}
      <DuplicateRecipesDialog
        open={duplicatesOpen}
        onOpenChange={setDuplicatesOpen}
        loading={duplicatesLoading}
        groups={duplicateGroups}
        stats={duplicateStats}
        selected={selectedForDeletion}
        onToggleSelected={(id) => setSelectedForDeletion((p) => ({ ...p, [id]: !p[id] }))}
        onDelete={handleDeleteSelectedDupes}
        deleting={deletingDupes}
      />
    </div>
  );
}
